<!--
  McpSetupModal.vue

  The MCP setup flow as a three-step plan: copy the agent-ready setup
  instructions, open an AI agent, paste them there — the agent registers
  Routine's local MCP server in its own configuration and verifies the
  connection. The instructions embed the endpoint and the signed-in account's
  bearer token, so there is nothing to configure by hand.
-->
<template>
  <div class="mcp-setup-modal">
    <BaseAlert v-if="setup.error" state="error" size="small">
      {{ setup.error }}
    </BaseAlert>

    <ol class="steps">
      <li class="step">
        <span class="step-number">1</span>
        <div class="step-body">
          <h4>Copy the instructions</h4>
          <p>
            They contain everything your AI agent or assistant needs to set up
            the Routine MCP, including your personal access token.
          </p>
          <BaseButton
            kind="main"
            type="primary"
            class="step-action"
            text="Copy instructions"
            :state="setupPrompt ? 'default' : 'disabled'"
            @click="copySetupPrompt"
          >
            <template #icon><CopyIcon /></template>
          </BaseButton>
          <p>
            ⚠️
            <strong>Do not share those instructions with anyone else.</strong>
          </p>
        </div>
      </li>

      <li class="step">
        <span class="step-number">2</span>
        <div class="step-body">
          <h4>Open your favorite AI agent or assistant</h4>
          <div class="agents">
            <span class="agent"><CursorAgentIcon />Cursor</span>
            <span class="agent"><ClaudeIcon />Claude Code</span>
            <span class="agent"><ClaudeIcon />Claude Desktop</span>
            <span class="agent"><CodexIcon />Codex</span>
            <span class="agent">any local MCP client</span>
          </div>
        </div>
      </li>

      <li class="step">
        <span class="step-number">3</span>
        <div class="step-body">
          <h4>Paste the instructions in the chat</h4>
          <p>
            Your AI agent or assistant sets up the Routine MCP server by
            following them, perhaps asking you a few preference questions.
          </p>
        </div>
      </li>
    </ol>

    <BaseAlert state="info" size="small">
      <InfoIcon class="note-icon" />
      <span>
        Keep Routine running and logged in whenever one of your AI agents is
        using the MCP server.
      </span>
    </BaseAlert>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ClaudeIcon from "@/components/icons/ClaudeIcon.vue";
import CodexIcon from "@/components/icons/CodexIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import CursorAgentIcon from "@/components/icons/CursorAgentIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import * as inject from "@/components/inject";
import { mcpSetupPrompt } from "@/mcp/setup-prompt";
import type { McpSetupResponse } from "@/mcp/types";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    ClaudeIcon,
    CodexIcon,
    CopyIcon,
    CursorAgentIcon,
    InfoIcon,
  },
  props: {
    setup: {
      type: Object as PropType<McpSetupResponse>,
      required: true,
    },
  },
  setup(props) {
    const controller = inject.controller();
    const setupPrompt = mcpSetupPrompt(props.setup);

    const copySetupPrompt = async () => {
      if (!setupPrompt) return;
      try {
        await navigator.clipboard.writeText(setupPrompt);
        controller.notifications.notify("MCP setup instructions copied");
      } catch {
        controller.notifications.notify(
          "Failed to copy the MCP setup instructions"
        );
      }
    };

    return {
      setupPrompt,
      copySetupPrompt,
    };
  },
});
</script>

<style scoped lang="scss">
.mcp-setup-modal {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  min-width: min(560px, calc(100vw - 96px));
  max-width: 640px;

  // BaseAlert's small variant is width: 100% with padding in content-box
  // sizing, which overflows the modal and makes it scroll horizontally.
  :deep(.alert) {
    box-sizing: border-box;
    margin-bottom: 0;
  }

  .steps {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    margin: 8px 0;
    padding: 0;
    list-style: none;
  }

  .step {
    display: flex;
    align-items: flex-start;
    column-gap: 14px;
  }

  .step-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
    @extend %font-caption-medium;
    @include themed() {
      border: 1px solid t($grey-15);
      background-color: t($grey-03);
      color: t($grey-60);
    }
  }

  .step-body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: 8px;
    // Center the first text line on the step number.
    padding-top: 2px;

    h4 {
      margin: 0;
      @extend %font-body-medium;
      @include themed() {
        color: t($primary);
      }
    }

    p {
      margin: 0;
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  .step-action {
    margin-top: 4px;
  }

  .agents {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .note-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-inline-end: 4px;
  }

  .agent {
    display: inline-flex;
    align-items: center;
    column-gap: 5px;
    border-radius: 999px;
    padding: 3px 10px;
    @extend %font-caption-medium;
    @include themed() {
      background-color: t($grey-05);
      color: t($grey-60);
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
